import { useCallback, useRef } from 'react';
import { isNil } from 'lodash';
import { ValuationsApproach } from 'common/types/valuation';
import getApproachTableName from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';

type Sheets = Record<string, { tableData?: unknown; reverseParse?: () => unknown } | undefined>;

// Comp fields the sheet rewrites from the selected periods, and that dependent approaches read.
const COMP_METRIC_FIELDS = ['ltm_revenue', 'ltm_ebitda', 'ntm_revenue', 'ntm_ebitda'] as const;

const readCompMetrics = (approach: ValuationsApproach) =>
  JSON.stringify(
    (approach.valuations_approach_gpc?.gpc_comparison ?? []).map(comp => [
      comp.cap_iq_id,
      ...COMP_METRIC_FIELDS.map(field => Number(comp[field])),
    ])
  );

interface UseFlushGPCAfterVersionSwitchParams {
  onCompMetricsChanged: (panelId: string) => void;
}

// After a statement version switch, writes the rebuilt public comps sheet back into its approach once,
// because in EV mode that sheet otherwise only writes its weighted values back when a cell is edited.
// If that changes the comp metrics, dependent approaches are asked to recalculate from them.
const useFlushGPCAfterVersionSwitch = ({ onCompMetricsChanged }: UseFlushGPCAfterVersionSwitchParams) => {
  const pendingStatementByPanelId = useRef(new Map<string, number>());

  const markVersionSwitch = useCallback((panelId: string, financialStatementId: number) => {
    pendingStatementByPanelId.current.set(panelId, financialStatementId);
  }, []);

  const flushSwitchedSheets = useCallback(
    (sheets: Sheets) => {
      Object.entries(sheets).forEach(([sheetName, sheet]) => {
        const approach = (sheet?.tableData as { approach?: ValuationsApproach } | undefined)?.approach;
        const panelId = approach?.panelId;
        const pendingStatementId = panelId ? pendingStatementByPanelId.current.get(panelId) : undefined;

        if (
          approach
          && panelId
          && !isNil(pendingStatementId)
          && pendingStatementId === approach.financial_statement
          && sheetName === getApproachTableName({ approach })
        ) {
          pendingStatementByPanelId.current.delete(panelId);
          const compMetricsBefore = readCompMetrics(approach);
          sheet?.reverseParse?.();

          if (readCompMetrics(approach) !== compMetricsBefore) {
            onCompMetricsChanged(panelId);
          }
        }
      });
    },
    [onCompMetricsChanged]
  );

  return { markVersionSwitch, flushSwitchedSheets };
};

export default useFlushGPCAfterVersionSwitch;
